<script setup>
// NOTICE: KAPTION-INTEGRATION
// Undo pending message wrapper - wraps entire message and makes it clickable to undo
// This component WRAPS the real Message component, not replaces it
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  /**
   * Remaining seconds before message sends
   */
  remainingSeconds: {
    type: Number,
    default: 15,
  },
  /**
   * Total undo window duration (for progress calculation)
   */
  totalDuration: {
    type: Number,
    default: 15,
  },
});

const emit = defineEmits(['undo']);

const { t } = useI18n();

// Track if we're in the undo delay period
const isUndoing = ref(false);

// Progress percentage (inverse - starts full, decreases to 0)
const progressPercent = computed(() => {
  return (props.remainingSeconds / props.totalDuration) * 100;
});

// NOTICE: KAPTION-INTEGRATION - Handle click to trigger undo (changed from hover-based)
const handleClick = () => {
  if (isUndoing.value) return;

  isUndoing.value = true;

  // Brief delay for visual feedback, then undo
  setTimeout(() => {
    emit('undo');
  }, 500);
};
</script>

<template>
  <div
    class="undo-wrapper relative group"
    :class="{ 'is-undoing': isUndoing }"
    @click="handleClick"
  >
    <!-- Hover overlay - covers entire message area -->
    <div
      class="absolute inset-0 z-10 rounded-lg transition-all duration-200 pointer-events-none"
      :class="[
        isUndoing
          ? 'bg-emerald-500/10 ring-2 ring-emerald-500/30'
          : 'group-hover:bg-amber-500/10 group-hover:ring-2 group-hover:ring-amber-500/30',
      ]"
    />

    <!-- Message content slot (the real Message component) - reduced opacity to show pending state -->
    <div class="relative opacity-60">
      <slot />
    </div>

    <!-- Undo hint footer below the message - right-aligned to match bubble -->
    <div class="flex justify-end ltr:mr-8 rtl:ml-8 -mt-1">
      <div
        class="undo-footer px-3 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-3 cursor-pointer"
        :class="[isUndoing ? 'bg-emerald-500/20' : 'bg-amber-500/20']"
        role="status"
        :aria-label="t('KAPTION_PLATFORM.UNDO_SEND.ARIA_UNDO_MESSAGE')"
      >
        <!-- Icon + countdown or undoing text -->
        <div class="flex items-center gap-1.5">
          <Icon
            :icon="isUndoing ? 'i-lucide-undo-2' : 'i-lucide-clock'"
            class="size-3 transition-all"
            :class="[
              isUndoing ? 'text-emerald-500' : 'text-amber-500 animate-pulse',
            ]"
          />
          <span
            class="text-xs font-medium whitespace-nowrap"
            :class="[
              isUndoing
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-amber-600 dark:text-amber-400',
            ]"
          >
            <template v-if="isUndoing">
              {{ t('KAPTION_PLATFORM.UNDO_SEND.UNDOING') }}
            </template>
            <template v-else>
              {{
                t('KAPTION_PLATFORM.UNDO_SEND.COUNTDOWN', {
                  seconds: remainingSeconds,
                })
              }}
            </template>
          </span>
        </div>

        <!-- Click hint -->
        <span
          v-if="!isUndoing"
          class="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap"
        >
          {{ t('KAPTION_PLATFORM.UNDO_SEND.CLICK_HINT') }}
          <span class="opacity-60">{{
            t('KAPTION_PLATFORM.UNDO_SEND.HOTKEY_HINT')
          }}</span>
        </span>

        <!-- Progress bar at bottom -->
        <div
          class="absolute bottom-0 left-0 h-0.5 transition-all duration-1000 ease-linear rounded-b-lg"
          :class="isUndoing ? 'bg-emerald-500' : 'bg-amber-500'"
          :style="{ width: `${progressPercent}%` }"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.undo-wrapper {
  cursor: pointer;
}

.undo-footer {
  position: relative;
  overflow: hidden;
}

/* Pulse animation for the clock icon */
@keyframes gentle-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

.animate-pulse {
  animation: gentle-pulse 1.5s ease-in-out infinite;
}
</style>
